import { useState, useEffect, useRef, useMemo } from 'react';
import {
  Plus, FileText, Upload, Trash2, AlertTriangle, Calendar, ExternalLink,
  Building2, Shield, ShieldCheck, Award, Leaf, FileCheck2, BadgeCheck, FilePlus,
  Loader2, X, FolderOpen,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { format, differenceInDays } from 'date-fns';
import { fr } from 'date-fns/locale';

// --- Catégories de documents prédéfinies (avec icône & couleur) ---
const CATEGORIES = [
  { key: 'Kbis',                  icon: Building2,  color: 'blue',    bg: 'bg-blue-50',    text: 'text-blue-700',    ring: 'ring-blue-200',    description: 'Extrait Kbis de la société' },
  { key: 'Assurance RC Pro',      icon: Shield,     color: 'sky',     bg: 'bg-sky-50',     text: 'text-sky-700',     ring: 'ring-sky-200',     description: 'Responsabilité Civile Professionnelle' },
  { key: 'Assurance Décennale',   icon: ShieldCheck,color: 'emerald', bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200', description: 'Garantie décennale BTP' },
  { key: 'Qualibat',              icon: Award,      color: 'amber',   bg: 'bg-amber-50',   text: 'text-amber-700',   ring: 'ring-amber-200',   description: 'Qualification Qualibat' },
  { key: 'RGE',                   icon: Leaf,       color: 'green',   bg: 'bg-green-50',   text: 'text-green-700',   ring: 'ring-green-200',   description: 'Reconnu Garant de l\'Environnement' },
  { key: 'Attestation URSSAF',    icon: FileCheck2, color: 'indigo',  bg: 'bg-indigo-50',  text: 'text-indigo-700',  ring: 'ring-indigo-200',  description: 'Vigilance / régularité sociale' },
  { key: 'Carte Professionnelle', icon: BadgeCheck, color: 'rose',    bg: 'bg-rose-50',    text: 'text-rose-700',    ring: 'ring-rose-200',    description: 'Carte BTP / professionnelle' },
  { key: 'Autre',                 icon: FilePlus,   color: 'slate',   bg: 'bg-slate-100',  text: 'text-slate-700',   ring: 'ring-slate-200',   description: 'Autres documents administratifs' },
];

const CAT_KEYS = CATEGORIES.map((c) => c.key);

export default function Administratif() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);
  const fileInputRef = useRef(null);
  const [newDoc, setNewDoc] = useState({
    nom: '',
    type_document: '',
    date_expiration: '',
    fichier_url: '',
    fichier_nom: '',
    notes: '',
  });

  useEffect(() => {
    document.title = 'Administratif - Reno Resolve';
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await api.get('/documents');
      setDocuments(response.data);
    } catch (error) {
      console.error('Erreur chargement documents:', error);
      toast.error('Erreur lors du chargement des documents');
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = (categoryKey) => {
    setNewDoc({
      nom: categoryKey && categoryKey !== 'Autre' ? `${categoryKey} ${new Date().getFullYear()}` : '',
      type_document: categoryKey || '',
      date_expiration: '',
      fichier_url: '',
      fichier_nom: '',
      notes: '',
    });
    setShowForm(true);
    // Auto-open the file picker just after the dialog is rendered
    setTimeout(() => fileInputRef.current?.click(), 250);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const response = await api.post('/upload/document', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setNewDoc((d) => ({
        ...d,
        fichier_url: response.data.url,
        fichier_nom: file.name,
        nom: d.nom || file.name.replace(/\.[^.]+$/, ''),
      }));
      toast.success('Fichier uploadé');
    } catch (error) {
      console.error('Erreur upload:', error);
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploading(false);
    }
  };

  const handleCreate = async () => {
    if (!newDoc.nom || !newDoc.type_document) {
      toast.error('Veuillez remplir le nom et le type de document');
      return;
    }
    setCreating(true);
    try {
      await api.post('/documents', {
        nom: newDoc.nom,
        type_document: newDoc.type_document,
        notes: newDoc.notes,
        fichier_url: newDoc.fichier_url,
        date_expiration: newDoc.date_expiration ? new Date(newDoc.date_expiration).toISOString() : null,
      });
      toast.success('Document ajouté');
      setShowForm(false);
      fetchDocuments();
    } catch (error) {
      console.error('Erreur création:', error);
      toast.error('Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      await api.delete(`/documents/${documentId}`);
      toast.success('Document supprimé');
      fetchDocuments();
    } catch (error) {
      console.error('Erreur suppression:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const getExpirationStatus = (dateExpiration) => {
    if (!dateExpiration) return null;
    const days = differenceInDays(new Date(dateExpiration), new Date());
    if (days < 0)  return { status: 'expired', label: 'Expiré',                       cls: 'bg-red-100 text-red-800',     ringCls: 'ring-red-300' };
    if (days <= 30) return { status: 'warning', label: `Expire dans ${days}j`,        cls: 'bg-amber-100 text-amber-800', ringCls: 'ring-amber-300' };
    return            { status: 'ok',      label: format(new Date(dateExpiration), 'dd/MM/yyyy', { locale: fr }), cls: 'bg-emerald-50 text-emerald-700', ringCls: 'ring-emerald-200' };
  };

  // Group documents by type
  const grouped = useMemo(() => {
    const map = new Map();
    CAT_KEYS.forEach((k) => map.set(k, []));
    documents.forEach((d) => {
      const k = CAT_KEYS.includes(d.type_document) ? d.type_document : 'Autre';
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(d);
    });
    return map;
  }, [documents]);

  const expiredDocs = documents.filter((d) => {
    const s = getExpirationStatus(d.date_expiration);
    return s && (s.status === 'expired' || s.status === 'warning');
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Documents Administratifs
          </h1>
          <p className="text-sm md:text-base text-slate-600 mt-1">
            {documents.length} document{documents.length > 1 ? 's' : ''} · classés par catégorie
          </p>
        </div>
        <Button onClick={() => openAddDialog('')} data-testid="add-document-btn">
          <Plus className="w-4 h-4 mr-2" />
          Ajouter un Document
        </Button>
      </div>

      {/* Alertes */}
      {expiredDocs.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm">
            <h4 className="font-semibold text-amber-900">{expiredDocs.length} document{expiredDocs.length > 1 ? 's' : ''} à renouveler</h4>
            <ul className="mt-1 space-y-0.5">
              {expiredDocs.map((d) => (
                <li key={d.document_id} className="text-amber-800">
                  • <span className="font-medium">{d.nom}</span> <span className="text-amber-600">({d.type_document})</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Catégories en tuiles */}
      <div className="space-y-4">
        {CATEGORIES.map((cat) => {
          const items = grouped.get(cat.key) || [];
          const Icon = cat.icon;
          return (
            <div key={cat.key} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid={`doc-cat-${cat.key}`}>
              {/* Header tuile */}
              <div className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-xl ${cat.bg} flex items-center justify-center ring-1 ${cat.ring}`}>
                    <Icon className={`w-5 h-5 ${cat.text}`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{cat.key}</h3>
                    <p className="text-xs text-slate-500">{cat.description} · {items.length} fichier{items.length > 1 ? 's' : ''}</p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className={`${cat.text} border-current hover:${cat.bg}`}
                  onClick={() => openAddDialog(cat.key)}
                  data-testid={`add-doc-${cat.key}`}
                >
                  <Upload className="w-3.5 h-3.5 mr-1.5" />
                  Insérer
                </Button>
              </div>

              {/* Liste */}
              {items.length === 0 ? (
                <button
                  onClick={() => openAddDialog(cat.key)}
                  className={`w-full px-4 md:px-6 py-6 text-sm text-slate-400 hover:bg-slate-50 hover:text-slate-700 flex items-center justify-center gap-2 border-t border-dashed border-slate-200 transition-colors`}
                  data-testid={`empty-${cat.key}`}
                >
                  <FolderOpen className="w-4 h-4" />
                  Aucun document — cliquez pour insérer un fichier
                </button>
              ) : (
                <div className="divide-y divide-slate-100">
                  {items.map((doc) => {
                    const exp = getExpirationStatus(doc.date_expiration);
                    return (
                      <div key={doc.document_id} className="grid grid-cols-1 md:grid-cols-[2fr_1fr_auto_auto] items-center gap-3 px-4 md:px-6 py-3 hover:bg-slate-50 transition-colors">
                        <div className="flex items-center gap-3 min-w-0">
                          <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900 truncate">{doc.nom}</p>
                            {doc.notes && <p className="text-xs text-slate-500 truncate">{doc.notes}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {exp && (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${exp.cls}`}>
                              <Calendar className="w-3 h-3" /> {exp.label}
                            </span>
                          )}
                        </div>
                        <div>
                          {doc.fichier_url && (
                            <a
                              href={`${process.env.REACT_APP_BACKEND_URL}${doc.fichier_url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                            >
                              <ExternalLink className="w-3.5 h-3.5" /> Voir
                            </a>
                          )}
                        </div>
                        <div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDelete(doc.document_id)}
                            data-testid={`delete-doc-${doc.document_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Dialog création */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {newDoc.type_document ? `Ajouter — ${newDoc.type_document}` : 'Ajouter un Document'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              className="hidden"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            />
            <Button
              type="button"
              variant="outline"
              className="w-full justify-center h-24 border-dashed border-2"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="upload-doc-btn"
            >
              {uploading ? (
                <span className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Upload en cours...</span>
              ) : newDoc.fichier_url ? (
                <span className="flex flex-col items-center gap-1 text-emerald-700">
                  <FileCheck2 className="w-6 h-6" />
                  <span className="text-sm font-medium">{newDoc.fichier_nom || 'Fichier prêt'}</span>
                  <span className="text-xs text-slate-500">Cliquez pour remplacer</span>
                </span>
              ) : (
                <span className="flex flex-col items-center gap-1 text-slate-500">
                  <Upload className="w-6 h-6" />
                  <span className="text-sm font-medium">Insérer un fichier</span>
                  <span className="text-xs">PDF, Word, image — max 10 Mo</span>
                </span>
              )}
            </Button>

            <div>
              <Label htmlFor="type">Type de document *</Label>
              <select
                id="type"
                className="mt-1 w-full h-10 rounded-md border border-slate-300 px-3 text-sm bg-white"
                value={newDoc.type_document}
                onChange={(e) => setNewDoc({ ...newDoc, type_document: e.target.value })}
                data-testid="doc-type-select"
              >
                <option value="">Sélectionner…</option>
                {CATEGORIES.map((c) => (
                  <option key={c.key} value={c.key}>{c.key}</option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="nom">Nom du document *</Label>
              <Input
                id="nom"
                placeholder="Ex: Kbis 2026"
                value={newDoc.nom}
                onChange={(e) => setNewDoc({ ...newDoc, nom: e.target.value })}
                data-testid="doc-nom-input"
              />
            </div>

            <div>
              <Label htmlFor="expiration">Date d'expiration (facultatif)</Label>
              <Input
                id="expiration"
                type="date"
                value={newDoc.date_expiration}
                onChange={(e) => setNewDoc({ ...newDoc, date_expiration: e.target.value })}
                data-testid="doc-exp-input"
              />
            </div>

            <div>
              <Label htmlFor="notes">Notes</Label>
              <Input
                id="notes"
                placeholder="Notes internes..."
                value={newDoc.notes}
                onChange={(e) => setNewDoc({ ...newDoc, notes: e.target.value })}
              />
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setShowForm(false)} disabled={creating}>
                <X className="w-4 h-4 mr-1" /> Annuler
              </Button>
              <Button onClick={handleCreate} disabled={creating || !newDoc.nom || !newDoc.type_document} data-testid="save-doc-btn">
                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Enregistrer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
